import { mulberry32, type Rng } from "../engine/rng";
import * as C from "../engine/constants";
import { DIST_SCALE, FIELD_W, FIELD_H, dirToField, fieldDir, makeSeeds, type FieldResult } from "./plateField";

/**
 * The wax shell of one ball.
 * - `color`: the satin wax paint (marble bands, pits, a fine mottle), a 1024×512 canvas (2048×1024 on the big ball)
 *   painted once.
 * - plates: a spherical Voronoi whose per-texel field (owner, neighbour, distance to the border) is
 *   computed off the main thread (plateField.worker.ts) when the ball spawns.
 * - Cracking never repaints anything: the shader reads the field plus a 768×1 plate-state texture (three texels
 *   per plate: hairline, gap width, tilt, open | fine crack width, fine hairline, sink, crushed | centre, lift)
 *   and draws thin anti-aliased cracks with dark clay in the gaps; the vertex shader lifts, tips and sinks each
 *   broken plate as a rigid piece of real thickness. A crack event only rewrites 3 KB.
 * - Each plate keeps the damage presses near it have done (`dmg`); crossing a level cracks, opens and loosens
 *   it, and past that it is crushed: it shatters along the finer crack layer into small fragments that stay
 *   on the ball and sink into the clay. The shell never falls off.
 * - Peeled chips (sticky drag) leave holes in a small 512×256 mask.
 */

export const PAINT_W = 1024;
export const PAINT_H = 512;
export const HOLE_W = 512;
export const HOLE_H = 256;
export const MAX_PLATES = 256;
/** state texture: G byte = gap half-width in field texels × GAP_SCALE */
export const GAP_SCALE = 32;
/** texels per plate in the state texture */
export const STATE_TEXELS = 3;
/** how fast a crack runs along the plate borders (rad/s): it visibly travels out from the finger */
export const FRONT_SPEED = 2.4;
/** the soft tip of a running crack (rad) */
export const FRONT_SOFT = 0.05;

export type Plate3D = {
  id: number;
  dir: [number, number, number]; // seed direction
  area: number; // solid angle (sr), known once the field is ready
  cls: 0 | 1 | 2; // small / medium / large slab
  thr: number; // crack resistance: small chips give first, slabs go late
  crazed: boolean; // hairline visible along its border
  hair: number; // hairline fade-in 0..1
  cracked: boolean;
  crackedAt: number;
  tilt: number; // -1..1 shading sign × strength (and how far a loose plate peels), easing toward tiltTarget
  tiltTarget: number;
  gap: number; // half gap width in field texels (eases toward gapTarget)
  gapTarget: number;
  flaked: boolean;
  pulled: boolean; // a chip of it was peeled off by a drag
  /** where its crack (or hairline) started: the crack only shows within `front` rad of this point */
  origin: [number, number, number];
  /** reach of the running crack front from `origin` (rad); eases toward frontTarget at frontSpeed */
  front: number;
  frontTarget: number;
  frontSpeed: number;
  /** damage done by presses near it: hair 0.12 → crack 0.3 → open 0.55 → loose 0.8 → crushed 1 → … */
  dmg: number;
  /** damage at the start of the current press (a press adds at most its share on top) */
  base: number;
  /** damage level reached: 0 intact, 1 hairline, 2 cracked, 3 open, 4 loose, 5 crushed, 6 ground in */
  level: number;
  /** crushed: fine cracks across the plate (half-width, fine-layer texels), easing toward fineTarget */
  fine: number;
  fineTarget: number;
  /** fine hairlines fading in before the fragments part */
  fineHair: number;
  /** how far the crushed fragments are pressed into the clay (0..1), easing toward sinkTarget */
  sink: number;
  sinkTarget: number;
  /** how far a loosened plate stands proud of the clay (0..1), easing toward liftTarget */
  lift: number;
  liftTarget: number;
  /** approximate angular radius (rad), from its area */
  rad: number;
};

const SMALL_W = 256, SMALL_H = 128;
/** a crack thins to a point over its last TIP_LEN rad behind the front */
export const TIP_LEN = 0.14;

export class ShellField {
  color: HTMLCanvasElement;
  seeds: Float32Array;
  /** seed of the finer crack layer (its cells are generated in the worker) */
  fineSeed: number;
  plates: Plate3D[];
  ready = false;
  ids: Uint8Array | null = null;
  dist: Uint8Array | null = null;
  /** size of the crack field maps (1024×512, or 2048×1024 on the big ball) */
  fieldW = FIELD_W;
  fieldH = FIELD_H;
  /** texture scale of the paint and the field (Pace.res) */
  res: number;
  fineDist: Uint8Array | null = null;
  hole: Uint8Array | null = null;
  holeDirty = false;
  /** STATE_TEXELS texels (RGBA8) per plate; see the header comment */
  state = new Uint8Array(MAX_PLATES * 4 * STATE_TEXELS);
  /** per plate: crack origin (xyz as 0..255) and front reach (a = front / π) */
  front = new Uint8Array(MAX_PLATES * 4);
  stateDirty = true;
  mud = 0;
  /** a hairline or gap is still easing toward its target */
  animating = false;
  /** crack opening ease (s); the crush switches to a slower one so the petals visibly open */
  tau = 0.06;
  /** hairline fade-in (s) */
  hairS = 0.08;
  private small: Uint8ClampedArray | null = null;
  private tmpDir = new Float32Array(3);

  variant: C.ShellVariant;
  private rng: Rng;

  constructor(variant: C.ShellVariant, rng: Rng, seedCount?: [number, number], res = 1) {
    this.variant = variant; this.rng = rng; this.res = res;
    this.color = document.createElement("canvas"); this.color.width = PAINT_W * res; this.color.height = PAINT_H * res;
    this.paintBase();
    this.seeds = makeSeeds(rng, seedCount);
    this.fineSeed = Math.floor(rng() * 4294967296);
    const n = Math.min(MAX_PLATES, this.seeds.length / 3);
    this.plates = [];
    for (let id = 0; id < n; id++) {
      this.plates.push({
        id, dir: [this.seeds[id * 3], this.seeds[id * 3 + 1], this.seeds[id * 3 + 2]], area: 0, cls: 1, thr: 1,
        crazed: false, hair: 0, cracked: false, crackedAt: 0, tilt: 0, tiltTarget: 0, gap: 0, gapTarget: 0, flaked: false, pulled: false,
        origin: [0, 0, 1], front: 0, frontTarget: 0, frontSpeed: FRONT_SPEED,
        dmg: 0, base: 0, level: 0, fine: 0, fineTarget: 0, fineHair: 0, sink: 0, sinkTarget: 0, lift: 0, liftTarget: 0, rad: 0.2,
      });
    }
    this.writeState();
  }

  /**
   * Satin wax: base colour, low-contrast marble bands, pits, and a fine mottle of slightly lighter and darker dye
   * (what a close look at dipped wax shows). Painted once (canvas row 0 = north pole); sizes in 1024-wide pixels × res.
   */
  private paintBase() {
    const g = this.color.getContext("2d")!, v = this.variant, rng = this.rng, k = this.res;
    const W = PAINT_W * k, H = PAINT_H * k;
    g.fillStyle = v.base; g.fillRect(0, 0, W, H);
    const bands = 3 + Math.floor(rng() * 3);
    // the marble on a layer of its own, faded out toward the texture poles (they map to the top and bottom caps of
    // the ball, where a band would pinch into a point): a soft fade, not a clip line that shows as a ring
    // (drawn on a layer wider than the map, so the blur sees each band carry on past the seam: no line at u = 0)
    const pad = 110 * k;
    const layer = document.createElement("canvas"); layer.width = W + 2 * pad; layer.height = H;
    const m = layer.getContext("2d")!;
    m.translate(pad, 0);
    m.lineCap = "round";
    m.filter = `blur(${2.5 * k}px)`; // dye in wax has soft edges (ignored where canvas filters are unsupported)
    for (let i = 0; i < bands; i++) {
      m.strokeStyle = v.swirl[i % 2]; m.globalAlpha = 0.45;
      const lw = (14 + rng() * 26) * 2 * k;
      m.lineWidth = lw;
      // a band that wanders up and down round the ball and meets itself at the seam (periodic in x)
      const y0 = rng() * H, a1 = (rng() - 0.5) * 220 * k, p1 = rng() * 6.283, a2 = (rng() - 0.5) * 90 * k, p2 = rng() * 6.283;
      m.beginPath();
      for (let x = -lw, n = 0; x <= W + lw; x += 8 * k, n++) {
        const t = (x / W) * 6.283, y = y0 + a1 * Math.sin(t + p1) + a2 * Math.sin(2 * t + p2);
        if (n === 0) m.moveTo(x, y); else m.lineTo(x, y);
      }
      m.stroke();
    }
    m.filter = "none"; m.globalAlpha = 1; m.globalCompositeOperation = "destination-in";
    const fade = m.createLinearGradient(0, 0, 0, H);
    fade.addColorStop(0.1, "rgba(0,0,0,0)"); fade.addColorStop(0.26, "rgba(0,0,0,1)");
    fade.addColorStop(0.74, "rgba(0,0,0,1)"); fade.addColorStop(0.9, "rgba(0,0,0,0)");
    m.fillStyle = fade; m.fillRect(-pad, 0, W + 2 * pad, H);
    g.drawImage(layer, pad, 0, W, H, 0, 0, W, H);
    layer.width = layer.height = 0; // let the big layer go right away
    g.fillStyle = "rgba(0,0,0,0.16)";
    for (let i = 0; i < 70; i++) { g.beginPath(); g.arc(rng() * W, rng() * H, (1 + rng() * 2) * k, 0, Math.PI * 2); g.fill(); }
    // the mottle: many faint specks, a pixel or two across (its own stream, so the layout above stays as it was)
    const mr = mulberry32(Math.floor(rng() * 4294967296));
    const specks = 500 * k * k;
    for (let i = 0; i < specks; i++) {
      g.fillStyle = mr() < 0.5 ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)";
      const r = (0.5 + mr() * 1.1) * k;
      g.beginPath(); g.arc(mr() * W, H * (0.04 + 0.92 * mr()), r, 0, Math.PI * 2); g.fill();
    }
  }

  /** A small CPU copy of the paint for peeled chips. Cheap, but it reads the canvas back, so it runs at idle time. */
  prepareSmall() {
    if (this.small) return;
    const cv = document.createElement("canvas"); cv.width = SMALL_W; cv.height = SMALL_H;
    const g = cv.getContext("2d", { willReadFrequently: true })!;
    g.drawImage(this.color, 0, 0, SMALL_W, SMALL_H);
    this.small = g.getImageData(0, 0, SMALL_W, SMALL_H).data;
  }

  /** The background thread finished this ball's crack field. */
  applyField(r: FieldResult) {
    this.ids = r.ids; this.dist = r.dist; this.fineDist = r.fine ?? null;
    this.fieldW = r.w ?? FIELD_W; this.fieldH = r.h ?? FIELD_H;
    let tot = 0;
    for (let i = 0; i < this.plates.length; i++) tot += r.area[i] ?? 0;
    const mean = tot / Math.max(1, this.plates.length);
    for (const p of this.plates) {
      p.area = r.area[p.id] ?? 0;
      const rel = p.area / (mean || 1);
      p.cls = rel < 0.55 ? 0 : rel < 1.5 ? 1 : 2;
      p.thr = 0.85 + 0.4 * this.rng(); // wax is uneven: some flakes give before their neighbours
      p.rad = Math.sqrt(p.area / Math.PI);
    }
    this.ready = true;
  }

  plateAt(x: number, y: number, z: number): Plate3D | null {
    if (!this.ready) return null;
    return this.plates[this.ids![dirToField(x, y, z, this.fieldW, this.fieldH) * 2]] ?? null;
  }

  /** The neighbour across the nearest border at a direction (for hairlines that follow a real crack line). */
  neighbourAt(x: number, y: number, z: number): Plate3D | null {
    if (!this.ready) return null;
    return this.plates[this.ids![dirToField(x, y, z, this.fieldW, this.fieldH) * 2 + 1]] ?? null;
  }

  /** The gap half-width plate `p` opens at unit direction (x, y, z): zero beyond its running front, thinning to a tip near it. */
  gapAt(p: Plate3D | undefined, x: number, y: number, z: number) {
    if (!p || !p.cracked || p.gap <= 0) return 0;
    const o = p.origin, d = o[0] * x + o[1] * y + o[2] * z;
    const ang = Math.acos(Math.max(-1, Math.min(1, d)));
    const behind = p.front - ang;
    if (behind <= 0) return 0;
    return p.gap * Math.min(1, behind / TIP_LEN);
  }

  /** 1 where field texel i (unit direction x, y, z) shows clay (an open gap or a peeled hole), else 0. Matches the shader's rule. */
  clayAt(i: number, x?: number, y?: number, z?: number) {
    if (!this.ready) return 0;
    const fW = this.fieldW, fH = this.fieldH;
    if (x === undefined) { const d = fieldDir(i % fW, (i / fW) | 0, this.tmpDir, fW, fH); x = d[0]; y = d[1]; z = d[2]; }
    const pa = this.plates[this.ids![i * 2]], pb = this.plates[this.ids![i * 2 + 1]];
    const gap = Math.max(this.gapAt(pa, x, y!, z!), this.gapAt(pb, x, y!, z!));
    if (gap > 0 && this.dist![i] / DIST_SCALE < gap) return 1;
    // a crushed plate: clay between its fragments, and the fragments themselves pressed part of the way in
    if (pa && pa.sink > 0) {
      if (this.fineDist && pa.fine > 0 && this.fineDist[i * 2] / DIST_SCALE < pa.fine) return 1;
      return 0.55 * pa.sink;
    }
    if (this.hole) {
      const x = i % fW, y = (i / fW) | 0;
      if (this.hole[((y * HOLE_H / fH) | 0) * HOLE_W + ((x * HOLE_W / fW) | 0)] > 128) return 1;
    }
    return 0;
  }

  /**
   * Hairlines on the borders near a direction (the plate there and its neighbour across the closest border),
   * a short run of `reach` rad around that point, not the plates' whole outlines.
   */
  craze(x: number, y: number, z: number, reach = 0.22) {
    const p = this.plateAt(x, y, z), q = this.neighbourAt(x, y, z);
    for (const t of [p, q]) {
      if (!t || t.cracked) continue;
      if (!t.crazed) { t.crazed = true; t.origin = [x, y, z]; t.front = 0; t.frontTarget = reach; t.frontSpeed = FRONT_SPEED * 0.8; }
      else this.reachTo(t, x, y, z, reach);
      this.animating = true;
    }
  }

  /** A hairline on plate p, running out from `from` (the finger) along its border for `reach` rad. */
  hairline(p: Plate3D, from: ArrayLike<number>, reach: number) {
    if (!p.crazed) { p.crazed = true; p.origin = [from[0], from[1], from[2]]; p.front = 0; p.frontTarget = Math.min(Math.PI, reach); p.frontSpeed = FRONT_SPEED * 0.8; }
    else this.reachTo(p, from[0], from[1], from[2], reach);
    this.animating = true;
  }

  /** Extends a plate's running front so its crack also covers `reach` rad around (x, y, z). */
  reachTo(p: Plate3D, x: number, y: number, z: number, reach: number) {
    const o = p.origin, ang = Math.acos(Math.max(-1, Math.min(1, o[0] * x + o[1] * y + o[2] * z)));
    const want = Math.min(Math.PI, ang + reach);
    if (want > p.frontTarget) { p.frontTarget = want; this.animating = true; }
  }

  /**
   * Cracks plate `p`, starting at `from` (the finger): the gap runs out along its borders from there,
   * `reach` rad at most for now (later events push it further), at `speed` rad/s.
   */
  crack(p: Plate3D, now: number, gap: number, tilt: number, rng: Rng, from?: ArrayLike<number>, reach = Math.PI, speed = FRONT_SPEED) {
    if (p.cracked) return false;
    p.cracked = true; p.crackedAt = now;
    if (from) {
      // a hairline already running here keeps its reach, so the crack never draws back
      const keep = p.crazed ? Math.max(0, p.front - Math.acos(Math.max(-1, Math.min(1, p.origin[0] * from[0] + p.origin[1] * from[1] + p.origin[2] * from[2])))) : 0;
      p.origin = [from[0], from[1], from[2]]; p.front = keep;
    } else { p.origin = [p.dir[0], p.dir[1], p.dir[2]]; p.front = 0; }
    p.crazed = true;
    p.frontTarget = Math.max(p.front, reach); p.frontSpeed = speed;
    p.gapTarget = Math.max(p.gapTarget, gap);
    p.tiltTarget = (rng() < 0.5 ? -1 : 1) * tilt * (0.6 + 0.4 * rng());
    this.animating = true;
    return true;
  }

  /** A loosened plate stands proud of the clay by `lift` (0..1 of the shader's LIFT_MAX). */
  raise(p: Plate3D, lift: number) {
    p.liftTarget = Math.max(0, Math.min(1, lift));
    this.animating = true;
  }

  /**
   * The plate is crushed under the finger: it shatters along the finer crack layer (hairlines first, then the
   * fragments part by `fine` texels) and is pressed `sink` (0..1) into the clay. It never comes off.
   */
  crush(p: Plate3D, fine: number, sink: number) {
    p.fineTarget = Math.max(p.fineTarget, fine);
    p.sinkTarget = Math.max(p.sinkTarget, Math.min(1, sink));
    this.animating = true;
  }

  widen(p: Plate3D, amount: number, max: number) {
    if (!p.cracked) return;
    p.gapTarget = Math.min(max, Math.max(p.gapTarget, p.gap) + amount);
    this.animating = true;
  }

  /**
   * Eases hairlines and gaps and runs the crack fronts out. Returns how far open cracks ran this step
   * (rad, summed over plates) so the sound can crackle along with them.
   */
  step(dt: number) {
    if (!this.animating) return 0;
    const k = 1 - Math.exp(-dt / this.tau);
    let moving = false, ran = 0;
    for (const p of this.plates) {
      if (p.crazed && p.hair < 1) { p.hair = Math.min(1, p.hair + dt / this.hairS); moving = true; }
      if (p.fineTarget > 0) {
        // shattering: fine hairlines run across the plate, then the fragments part and sink
        if (p.fineHair < 1) { p.fineHair = Math.min(1, p.fineHair + dt / 0.07); moving = true; }
        const tf = p.fineHair >= 1 ? p.fineTarget : 0;
        if (Math.abs(tf - p.fine) > 0.01) { p.fine += (tf - p.fine) * k; moving = true; } else p.fine = tf;
        if (Math.abs(p.sinkTarget - p.sink) > 0.005) { p.sink += (p.sinkTarget - p.sink) * k * 0.6; moving = true; } else p.sink = p.sinkTarget;
      }
      if (Math.abs(p.liftTarget - p.lift) > 0.005) { p.lift += (p.liftTarget - p.lift) * k * 0.8; moving = true; } else p.lift = p.liftTarget;
      if (Math.abs(p.tiltTarget - p.tilt) > 0.005) { p.tilt += (p.tiltTarget - p.tilt) * k * 0.8; moving = true; } else p.tilt = p.tiltTarget;
      if (Math.abs(p.gapTarget - p.gap) > 0.01) { p.gap += (p.gapTarget - p.gap) * k; moving = true; } else p.gap = p.gapTarget;
      if (p.front < p.frontTarget) {
        // a crack runs fast at first, then slows as it reaches its end
        const left = p.frontTarget - p.front, v = p.frontSpeed * (0.35 + 0.65 * Math.min(1, left / 0.4));
        const d = Math.min(left, v * dt);
        p.front += d; moving = true;
        if (p.cracked) ran += d;
      }
    }
    this.animating = moving;
    this.writeState();
    return ran;
  }

  writeState() {
    const s = this.state;
    for (const p of this.plates) {
      const o = p.id * 4, o2 = p.id * 4 * STATE_TEXELS;
      s[o2] = Math.round(p.hair * 255);
      s[o2 + 1] = Math.min(255, Math.round((p.cracked ? p.gap : 0) * GAP_SCALE));
      s[o2 + 2] = Math.max(0, Math.min(255, Math.round(128 + p.tilt * 100)));
      s[o2 + 3] = p.cracked ? 255 : 0;
      s[o2 + 4] = Math.min(255, Math.round(p.fine * GAP_SCALE));
      s[o2 + 5] = Math.round(p.fineHair * 255);
      s[o2 + 6] = Math.round(p.sink * 255);
      s[o2 + 7] = p.fineTarget > 0 ? 255 : 0;
      s[o2 + 8] = Math.round((p.dir[0] * 0.5 + 0.5) * 255);
      s[o2 + 9] = Math.round((p.dir[1] * 0.5 + 0.5) * 255);
      s[o2 + 10] = Math.round((p.dir[2] * 0.5 + 0.5) * 255);
      s[o2 + 11] = Math.round(Math.max(0, Math.min(1, p.lift)) * 255);
      const f = this.front;
      f[o] = Math.round((p.origin[0] * 0.5 + 0.5) * 255);
      f[o + 1] = Math.round((p.origin[1] * 0.5 + 0.5) * 255);
      f[o + 2] = Math.round((p.origin[2] * 0.5 + 0.5) * 255);
      f[o + 3] = Math.round(Math.max(0, Math.min(1, p.front / Math.PI)) * 255);
    }
    this.stateDirty = true;
  }

  /**
   * Tears a peeled chip out of the shell: clay inside the jagged outline `radii` (rad, evenly spaced
   * in angle around `a` in the t1/t2 frame), a whitened rim just outside it. Soft-edged mask so the
   * shader anti-aliases the hole edge.
   */
  addHole(a: ArrayLike<number>, t1: ArrayLike<number>, t2: ArrayLike<number>, radii: number[]) {
    const hole = this.hole ?? (this.hole = new Uint8Array(HOLE_W * HOLE_H));
    const M = radii.length, SOFT = 0.03;
    const rMax = Math.max(...radii) + SOFT;
    const c = dirToField(a[0], a[1], a[2], HOLE_W, HOLE_H), cx = c % HOLE_W, cy = (c / HOLE_W) | 0;
    const rows = Math.ceil((rMax / Math.PI) * HOLE_H) + 1;
    const lo = Math.max(0, cy - rows), hi = Math.min(HOLE_H - 1, cy + rows);
    const d = new Float32Array(3);
    for (let y = lo; y <= hi; y++) {
      const st = Math.sin(((y + 0.5) / HOLE_H) * Math.PI);
      const cols = st <= rMax ? HOLE_W / 2 : Math.ceil((rMax / st / (Math.PI * 2)) * HOLE_W) + 1;
      for (let k = -cols; k < cols; k++) {
        const x = (cx + k + HOLE_W * 2) % HOLE_W, i = y * HOLE_W + x;
        fieldDir(x, y, d, HOLE_W, HOLE_H);
        const dn = d[0] * a[0] + d[1] * a[1] + d[2] * a[2];
        if (dn <= 0) continue;
        const u = d[0] * t1[0] + d[1] * t1[1] + d[2] * t1[2], v = d[0] * t2[0] + d[1] * t2[1] + d[2] * t2[2];
        const ang = Math.atan2(Math.hypot(u, v), dn);
        if (ang > rMax) continue;
        let f = (Math.atan2(v, u) / (Math.PI * 2)) * M; if (f < 0) f += M;
        const k0 = Math.floor(f) % M, k1 = (k0 + 1) % M;
        const lim = radii[k0] + (radii[k1] - radii[k0]) * (f - Math.floor(f));
        const val = Math.round(Math.max(0, Math.min(1, 0.5 + (lim - ang) / (2 * SOFT))) * 255);
        if (val > hole[i]) hole[i] = val;
      }
    }
    this.holeDirty = true;
  }

  /** Copies the paint around `a` (t1/t2 frame, half-size s rad) into an n×n canvas for a peeled chip. */
  cutChip(a: ArrayLike<number>, t1: ArrayLike<number>, t2: ArrayLike<number>, s: number, n = 32): HTMLCanvasElement {
    this.prepareSmall();
    const cv = document.createElement("canvas"); cv.width = cv.height = n;
    const g = cv.getContext("2d")!, img = g.createImageData(n, n), src = this.small!, dst = img.data;
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const u = ((i + 0.5) / n * 2 - 1) * s, v = ((j + 0.5) / n * 2 - 1) * s;
      const x = a[0] + t1[0] * u + t2[0] * v, y = a[1] + t1[1] * u + t2[1] * v, z = a[2] + t1[2] * u + t2[2] * v;
      const l = Math.hypot(x, y, z) || 1;
      let phi = Math.atan2(z / l, -x / l); if (phi < 0) phi += Math.PI * 2;
      const th = Math.acos(Math.max(-1, Math.min(1, y / l)));
      const sx = Math.min(SMALL_W - 1, Math.floor((phi / (Math.PI * 2)) * SMALL_W)), sy = Math.min(SMALL_H - 1, Math.floor((th / Math.PI) * SMALL_H));
      const si = (sy * SMALL_W + sx) * 4, di = (j * n + i) * 4;
      dst[di] = src[si]; dst[di + 1] = src[si + 1]; dst[di + 2] = src[si + 2]; dst[di + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    return cv;
  }

}

export { FIELD_W, FIELD_H };

